import { Pressable, Text, View } from 'react-native'
import { useTranslation } from 'react-i18next'
import { Controller } from 'react-hook-form'
import type { Control } from 'react-hook-form'
import { TextField } from '@chillout/ui'
import type { LoyaltyConfig } from './useLoyaltyConfig'

interface EarningRuleSectionProps {
  control: Control<LoyaltyConfig>
  stepNumber: number
}

export function EarningRuleSection({ control, stepNumber }: EarningRuleSectionProps) {
  const { t } = useTranslation()

  return (
    <View className="gap-5 rounded-xl border border-border bg-canvas-card p-6">
      <View>
        <View className="flex-row items-baseline gap-2">
          <Text className="text-lg font-semibold text-primary">{stepNumber}.</Text>
          <Text className="text-lg font-semibold text-primary">{t('loyalty.earningRule.sectionTitle')}</Text>
        </View>
        <Text className="mt-1 text-sm text-secondary">{t('loyalty.earningRule.subtitle')}</Text>
      </View>

      <Controller
        control={control}
        name="earningMethod"
        render={({ field: methodField }) => {
          const isSpendBased = methodField.value === 'spend_based'
          return (
            <View className="flex-col gap-3 lg:flex-row 2xl:flex-col 3xl:flex-row">
              <EarningMethodCard
                selected={!isSpendBased}
                onPress={() => methodField.onChange('per_meal')}
                accessibilityLabel={t('loyalty.earningRule.perMeal')}
              >
                <Text className="text-base font-semibold text-primary">{t('loyalty.earningRule.perMeal')}</Text>
                <Text className="mt-1 text-sm text-secondary">{t('loyalty.earningRule.perMealDescription')}</Text>
              </EarningMethodCard>

              <EarningMethodCard
                selected={isSpendBased}
                onPress={() => methodField.onChange('spend_based')}
                accessibilityLabel={t('loyalty.earningRule.spendBased')}
              >
                <Text className="text-base font-semibold text-primary">{t('loyalty.earningRule.spendBased')}</Text>
                <Text className="mt-1 text-sm text-secondary">{t('loyalty.earningRule.spendBasedDescription')}</Text>

                <View className={`mt-4 ${!isSpendBased ? 'opacity-40' : ''}`}>
                  <Controller
                    control={control}
                    name="amountPerStamp"
                    render={({ field: amountField, fieldState }) => {
                      const examplePurchase = amountField.value <= 250 ? 250 : amountField.value * 2
                      const exampleStamps = amountField.value > 0 ? Math.floor(examplePurchase / amountField.value) : 0
                      return (
                        <>
                          <TextField
                            label={t('loyalty.earningRule.amountPerStamp')}
                            value={String(amountField.value)}
                            onChangeText={(val) => {
                              const digits = val.replace(/[^0-9]/g, '')
                              amountField.onChange(digits === '' ? 0 : parseInt(digits, 10))
                            }}
                            onBlur={amountField.onBlur}
                            error={fieldState.error?.message}
                            keyboardType="numeric"
                            suffix={t('loyalty.earningRule.currency')}
                            accessibilityLabel={t('loyalty.earningRule.amountPerStamp')}
                            editable={isSpendBased}
                          />
                          <Text className="mt-2 text-sm text-secondary">
                            {exampleStamps === 1
                              ? t('loyalty.earningRule.exampleSingular', { amount: examplePurchase })
                              : t('loyalty.earningRule.example', { amount: examplePurchase, stamps: exampleStamps })}
                          </Text>
                        </>
                      )
                    }}
                  />
                </View>
              </EarningMethodCard>
            </View>
          )
        }}
      />
    </View>
  )
}

interface EarningMethodCardProps {
  selected: boolean
  onPress: () => void
  accessibilityLabel: string
  children: React.ReactNode
}

function EarningMethodCard({ selected, onPress, accessibilityLabel, children }: EarningMethodCardProps) {
  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="radio"
      accessibilityState={{ checked: selected }}
      accessibilityLabel={accessibilityLabel}
      className={`w-full lg:flex-1 2xl:w-full 3xl:flex-1 flex-row gap-3 rounded-lg border p-4 ${selected ? 'border-accent' : 'border-border'}`}
    >
      <View className={`mt-0.5 h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 ${selected ? 'border-accent' : 'border-border'}`}>
        {selected && <View className="h-2.5 w-2.5 rounded-full bg-accent" />}
      </View>
      <View className="min-w-0 flex-1">{children}</View>
    </Pressable>
  )
}